import { useAtomValue } from 'jotai';

import {
  chronicleConnectionContextAtom,
  chronicleConnectionStatusAtom,
} from '@/atoms/chronicle';
import { useIsDesktopChronicleEnabled } from '@/features/flags';
import { useRestoreLinkedDevice } from '@/hooks/desktop';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';

import { useSyncHardwareProfileMetadata } from './useSyncHardwareProfileMetadata';

/**
 * Restore the linked-device serial from `/hardware-device/` on app load so
 * `auto_bulk_sync` fires on any page, not only after the device page mounts.
 *
 * The reconciler keys `sync_tracking` on the device serial and skips when it's
 * absent. The BLE-decoupled restore (`useRestoreLinkedDevice`) was previously
 * mounted only on the device page, so a USB-connected device opened straight to
 * the session list (no live BLE to supply the serial) never auto-synced. This
 * is gated on auth + the Chronicle flag and decoupled from BLE auto-connect;
 * TanStack dedups with the device-page mount of `useRestoreLinkedDevice`.
 */
export function useChronicleRestoreLinkedDevice() {
  const { isAuthenticated, isLoading: isAuthLoading } = useIsAuthenticated();
  const isChronicleEnabled = useIsDesktopChronicleEnabled();
  const { linkedDeviceId } = useAtomValue(chronicleConnectionContextAtom);
  const connectionStatus = useAtomValue(chronicleConnectionStatusAtom);

  const isEnabled = isAuthenticated && !isAuthLoading && isChronicleEnabled;

  useRestoreLinkedDevice({
    linkedDeviceId,
    connectionStatus,
    enabled: isEnabled,
  });

  // Same linked-device identity, forwarded to the Amplitude profile and the
  // Intercom contact — the restore above is what populates it with the remote
  // powered off. Shares the gate: the Chronicle flag has to be able to stop the
  // egress, and the atom is populated independently of it.
  useSyncHardwareProfileMetadata(isEnabled);
}
